/**
 * Twilio Service
 * 
 * This service handles the integration with Twilio for voice and video calls.
 * It provides functions to create and manage calls between healthcare providers and patients.
 */

import axios from 'axios';

// In a real implementation, these would be environment variables
const TWILIO_API_BASE_URL = process.env.REACT_APP_TWILIO_API_BASE_URL || 'http://localhost:3001/twilio';

/**
 * Get a Twilio access token for video calls
 * @param {string} identity - The identity of the user (e.g., 'doctor_123')
 * @param {string} roomName - The name of the video room to join
 * @returns {Promise<string>} - The access token
 */
export const getVideoToken = async (identity, roomName) => {
  try {
    const response = await axios.post(`${TWILIO_API_BASE_URL}/video-token`, {
      identity,
      roomName
    });
    return response.data.token;
  } catch (error) {
    console.error('Error getting video token:', error);
    throw new Error('Failed to get video access token');
  }
};

/**
 * Get a Twilio access token for voice calls
 * @param {string} identity - The identity of the user (e.g., 'doctor_123')
 * @returns {Promise<string>} - The access token
 */
export const getVoiceToken = async (identity) => {
  try {
    const response = await axios.post(`${TWILIO_API_BASE_URL}/voice-token`, {
      identity
    });
    return response.data.token;
  } catch (error) {
    console.error('Error getting voice token:', error);
    throw new Error('Failed to get voice access token');
  }
};

/**
 * Create a new video room
 * @param {string} roomName - The name of the video room to create
 * @returns {Promise<Object>} - The room details
 */
export const createVideoRoom = async (roomName) => {
  try {
    const response = await axios.post(`${TWILIO_API_BASE_URL}/create-room`, {
      roomName
    });
    return response.data.room;
  } catch (error) {
    console.error('Error creating video room:', error);
    throw new Error('Failed to create video room');
  }
};

/**
 * Initiate a voice call to a patient
 * @param {string} patientPhone - The patient's phone number
 * @param {string} doctorIdentity - The doctor's identity
 * @returns {Promise<Object>} - The call details
 */
export const initiateVoiceCall = async (patientPhone, doctorIdentity) => {
  try {
    const response = await axios.post(`${TWILIO_API_BASE_URL}/make-call`, {
      to: patientPhone,
      from: doctorIdentity
    });
    return response.data.call;
  } catch (error) {
    console.error('Error initiating voice call:', error);
    throw new Error('Failed to initiate voice call');
  }
};

/**
 * Send an SMS with a video call link to a patient
 * @param {string} patientPhone - The patient's phone number
 * @param {string} roomLink - The link to the video room
 * @returns {Promise<Object>} - The message details
 */
export const sendVideoCallLink = async (patientPhone, roomLink) => {
  try {
    const response = await axios.post(`${TWILIO_API_BASE_URL}/send-sms`, {
      to: patientPhone,
      body: `Your healthcare provider has invited you to a video call. Please click the link to join: ${roomLink}`
    });
    return response.data.message;
  } catch (error) {
    console.error('Error sending video call link:', error);
    throw new Error('Failed to send video call link');
  }
};

/**
 * End an active call
 * @param {string} callSid - The SID of the call to end
 * @returns {Promise<Object>} - The result of ending the call
 */
export const endCall = async (callSid) => {
  try {
    const response = await axios.post(`${TWILIO_API_BASE_URL}/end-call`, {
      callSid
    });
    return response.data;
  } catch (error) {
    console.error('Error ending call:', error);
    throw new Error('Failed to end call');
  }
}; 